/**
 * Local-first CONSUMPTION bridge: grades a lesson with `learning-blocks`, derives
 * the gamification events and applies them with `gamification-core`. Immediate
 * feedback on the client; the server RE-GRADES and is the authority (grading.rs).
 */

import { dayKeyUTC } from "../gamification/streaks";
import { applyEvents } from "../gamification/engine";
import type { Awards, GameEvent, PlayerState } from "../gamification/types";
import {
  type AntifraudResult,
  type AttemptSubmission,
  verifyAttempt,
} from "../learning-blocks/antifraud";
import type { BlockRegistry } from "../learning-blocks/registry";
import type { AuthoredBlock, BlockResponse, LessonResult } from "../learning-blocks/types";
import { AULA_EVENTS, aulaGamificationConfig, DEFAULT_LESSON_XP } from "./gamification";

/** Passing threshold for a lesson (matches the server: 0.6). */
export const LESSON_PASS_THRESHOLD = 0.6;

/** Result of consuming a lesson (for UI + persistence + gamification). */
export interface LessonOutcome {
  lessonId: string;
  courseId: string;
  /** Score 0..1 recalculated locally (the server re-verifies it). */
  score: number;
  passed: boolean;
  perfect: boolean;
  /** Configured lesson XP (authoritative from the content). */
  xpReward: number;
  /** Did completing this lesson complete the course? */
  courseCompleted: boolean;
  /** Detailed per-block grading (for review). */
  result: LessonResult;
  /** Local antifraud signals (informational; the server is the authority). */
  fraudFlags: AntifraudResult["flags"];
}

/** Builds the submission for `verifyAttempt` from the responses. */
export function buildAttemptSubmission(
  lessonId: string,
  responses: BlockResponse[],
  opts: { claimedScore?: number; startedAt?: number; finishedAt?: number } = {},
): AttemptSubmission {
  return {
    lessonId,
    responses,
    claimedScore: opts.claimedScore,
    startedAt: opts.startedAt,
    finishedAt: opts.finishedAt,
  };
}

/** Grades a lesson locally (same logic the server re-verifies with). */
export function gradeLessonAttempt(
  registry: BlockRegistry,
  blocks: readonly AuthoredBlock[],
  submission: AttemptSubmission,
): AntifraudResult {
  return verifyAttempt(registry, blocks, submission);
}

/** Derives the gamification events for a completed lesson. */
export function lessonCompletionEvents(
  outcome: LessonOutcome,
  at: number = Date.now(),
): GameEvent[] {
  if (!outcome.passed) {
    return [];
  }
  const day = dayKeyUTC(at);
  const events: GameEvent[] = [
    {
      type: AULA_EVENTS.lessonCompleted,
      at,
      day,
      value: outcome.xpReward || DEFAULT_LESSON_XP,
      meta: { lessonId: outcome.lessonId, courseId: outcome.courseId },
    },
  ];
  if (outcome.perfect) {
    events.push({ type: AULA_EVENTS.lessonPerfect, at, day, meta: { lessonId: outcome.lessonId } });
  }
  if (outcome.courseCompleted) {
    events.push({
      type: AULA_EVENTS.courseCompleted,
      at,
      day,
      meta: { courseId: outcome.courseId },
    });
  }
  return events;
}

/**
 * "Wired gamification": applies a lesson's result to the player's state
 * with Aula's config. Returns the new state and the rewards
 * (XP, badges, streak, level-up) so the UI can celebrate them.
 */
export function applyLessonCompletion(
  state: PlayerState,
  outcome: LessonOutcome,
  at: number = Date.now(),
): { state: PlayerState; awards: Awards } {
  const events = lessonCompletionEvents(outcome, at);
  return applyEvents(state, events, aulaGamificationConfig);
}

/**
 * Builds a `LessonOutcome` from lesson grading and configured
 * XP. `courseProgress` indicates whether the course is now completed.
 */
export function toLessonOutcome(params: {
  lessonId: string;
  courseId: string;
  xpReward: number;
  attempt: AntifraudResult;
  courseCompleted?: boolean;
}): LessonOutcome {
  const { result, flags } = params.attempt;
  const passed = result.score >= LESSON_PASS_THRESHOLD;
  return {
    lessonId: params.lessonId,
    courseId: params.courseId,
    score: result.score,
    passed,
    perfect: result.score >= 1,
    xpReward: params.xpReward,
    courseCompleted: params.courseCompleted ?? false,
    result,
    fraudFlags: flags,
  };
}
